import React, { useMemo } from 'react';

import {
  Box,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { EditBase, usePermissions, useRecordContext } from 'react-admin';

import CancelIcon from '@mui/icons-material/Cancel';

import { MemoryRouter, useLocation } from 'react-router-dom';

import entityTypes, { EntityTypes, KnownEntityTypes, resourceFromEntityTypes } from '../../constants/entityTypes';
import { IndividualEditContent } from '../individual/IndividualEdit';
import ReportModal from '../../customFields/ReportModal';
import { AccountEntityEditContent } from '../accountEntity/AccountEntityEdit';
import { CarEditContent } from '../car/CarEdit';
import { BusinessEditContent } from '../business/BusinessEdit';
import { ProductsEditContent } from '../product/ProductEdit';
import { ScopedEmbeddedViewContextProvider } from '../utils/ScopedEmbeddedViewContext';
import CreateComment from '../comment/CreateComment';
import resourceTypes from '../../constants/resourceTypes';
import actions, { Actions } from '../../constants/actions';
import hasAccess from '../../utilities/hasAccess';

const mapEntityEditContent: Record<KnownEntityTypes, React.FC> = {
  [entityTypes.INDIVIDUAL]: IndividualEditContent,
  [entityTypes.ACCOUNT]: AccountEntityEditContent,
  [entityTypes.CAR]: CarEditContent,
  [entityTypes.BUSINESS]: BusinessEditContent,
  [entityTypes.PRODUCT]: ProductsEditContent,
};

const EntityOverlay = ({
  defaultEntries,
  closeDialog,
  open,
  caseId,
}: {
  defaultEntries: string;
  closeDialog: () => void;
  open: boolean;
  caseId: string;
}) => {
  const location = useLocation();
  const entityContext = useRecordContext<{
    id: string;
    type: EntityTypes;
  }>();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const isQuiteLargeScreen = useMediaQuery(theme.breakpoints.down('xl'));
  const { permissions } = usePermissions<Actions[]>();

  const leftSpace = useMemo(() => {
    if (isSmallScreen) return 0;
    if (isMediumScreen) return 100;
    if (isQuiteLargeScreen) return 200;
    return 300;
  }, [isMediumScreen, isQuiteLargeScreen, isSmallScreen]);

  const scopedContext = useMemo(() => {
    if (!entityContext) return null;
    return {
      isScopedEmbedded: true,
      scopedResource: resourceFromEntityTypes[entityContext.type as KnownEntityTypes],
      scopedRecordId: entityContext.id,
      scopedParentRecordId: caseId,
      scopedParentResource: resourceTypes.CASE,
    };
  }, [entityContext, caseId]);

  if (!entityContext || !scopedContext) return null;

  const {
    type,
    id,
  } = entityContext;

  const EntityEditContent = mapEntityEditContent[type as KnownEntityTypes];

  return (
    <Drawer
      open={open}
      onClose={closeDialog}
      anchor="right"
      PaperProps={{
        sx: {
          left: leftSpace,
          bgcolor: theme.palette.background.default,
        },
      }}
    >
      <Box display="flex" justifyContent="flex-end" padding={4}>
        <IconButton onClick={closeDialog}>
          <CancelIcon />
        </IconButton>
      </Box>
      <MemoryRouter
        initialEntries={[`${location.pathname.replace(/\/+$/, '')}/${defaultEntries}`]}
      >
        <ScopedEmbeddedViewContextProvider value={scopedContext}>
          <EditBase
            id={id}
            resource={resourceFromEntityTypes[type as KnownEntityTypes]}
            redirect={false}
          >
            <Box display="flex" justifyContent="flex-end" paddingX={4}>
              <ReportModal />
            </Box>
            <EntityEditContent />
          </EditBase>
        </ScopedEmbeddedViewContextProvider>
      </MemoryRouter>
      {hasAccess(permissions, actions.CASE_ADD_COMMENT) && (
        <Box position="fixed" bottom="48px" right="48px">
          <CreateComment customLabel="Add comment to case" caseId={caseId} floating />
        </Box>
      ) }
    </Drawer>
  );
};

export default EntityOverlay;
